// #1664 — Tutorial parte 2 "Sincroniza tus camas" (.claude/specs/1664-tutorial-sincronizar-camas/).
// Decisiones PURAS (sin React) para poder testearlas y citarlas desde Turno.js. La parte 1
// (`tutorialPrimerDoc.js`) no se toca: de ella solo se importa `sistemaTutorialActivo`.
import { sistemaTutorialActivo } from "./tutorialPrimerDoc";

// Documentos desde los que se impone (espejo de `_DOCUMENTOS_TUTORIAL_CAMAS` en
// backend/app/user_api/proxima_pantalla.py).
export const DOCUMENTOS_TUTORIAL_CAMAS = 3;

// La parte de la decisión que depende SOLO de la cuenta: "a esta persona el tutorial obligatorio de camas le
// va a salir (o ya le toca)". La usa `decidirTutorialCamas` y también `camasPendiente` de app-celular.js (#1686
// D14), que espera a este tutorial: una sola regla para las dos, así no se separan. Sin Modo Turno el tutorial
// nunca abre (vive en /turno, que `ProtectedRoute` exige con `can_access_turno`, y el back lo pide igual en
// `proxima_pantalla`); si el celular lo esperara igual, esas cuentas no veían nunca su pop-up (W3 del revisor
// p4 de #1664, 17-oct: 2 cuentas en prod con 3+ documentos y sin Modo Turno). El superuser sale por
// `sistemaTutorialActivo`. Campo AUSENTE (backend viejo) = "no".
// Cubre R25 — .claude/specs/1664-tutorial-sincronizar-camas/ · R27 v2 — .claude/specs/1686-checklist-uso-celular/
export const tutorialCamasPendiente = (user) =>
  user?.id != null &&
  Object.prototype.hasOwnProperty.call(user, "tutorial_camas_completado_at") &&
  user.tutorial_camas_completado_at === null &&
  sistemaTutorialActivo(user) &&
  Boolean(user.can_access_turno) &&
  user.ha_sincronizado_camas !== true;

// Cubre R8, R9, R10, R11, R12, R25 — .claude/specs/1664-tutorial-sincronizar-camas/
// - La cuenta tiene que estar en `tutorialCamasPendiente` (arriba); el resto es de esta carga.
// - El campo AUSENTE (backend viejo) es "no": solo abre con `tutorial_camas_completado_at === null`.
// - `documentosAlEntrar` es el `documentos_generados` del PRIMER /me de la carga (R12): llegar al
//   tercer documento a mitad de la carga no interrumpe la nota; sale en la carga siguiente.
// - `computador` es `esComputadorParaTutorial` de #1660 (R10: en el celular y la tablet espera).
// - `otraCapa` / `otraCapaEnEstaCarga`: nunca dos capas de bienvenida en la misma carga (R11).
// - `hechoEnEstaCarga`: completado en esta carga aunque el POST falle (R20).
// - R25: quien ya aplicó una sincronización de camas (`ha_sincronizado_camas`, R24) no lo recibe.
export const decidirTutorialCamas = ({
  user,
  documentosAlEntrar,
  computador,
  selectedOwner = null,
  otraCapa = false,
  otraCapaEnEstaCarga = false,
  hechoEnEstaCarga = false,
}) =>
  tutorialCamasPendiente(user) &&
  selectedOwner == null &&
  Boolean(computador) &&
  typeof documentosAlEntrar === "number" &&
  documentosAlEntrar >= DOCUMENTOS_TUTORIAL_CAMAS &&
  !otraCapa &&
  !otraCapaEnEstaCarga &&
  !hechoEnEstaCarga;

// Cubre R15 (tema de la app) y R23 (repaso con X). Ningún parámetro salta capítulos ni lleva al
// cierre (R13): los e2e aceleran el reloj, no la página.
// Menú Tutoriales: revisor v1 (19-oct): `sinNotas` agrega `portada=neutra` (sin «Hiciste tus primeras notas clínicas»
// y sin el capítulo 0 con confeti y voz «Bien hecho…»; lo aplica tools/tutorial_build/build_camas.py).
export const urlTutorialCamas = ({ tema, modo, sinNotas = false }) =>
  `/tutorial/sincronizar-camas.html?theme=${tema === "light" ? "light" : "dark"}&modo=${modo === "repaso" ? "repaso" : "obligatorio"}${sinNotas ? "&portada=neutra" : ""}`;

// «Sin felicitación si no hay notas» (Sebastián, 19-oct): el tutorial de camas felicita por las primeras notas
// clínicas; una cuenta con 0 documentos generados no las hizo. Solo el 0 numérico: sin el dato no se asume nada.
export const sinNotasEnCuenta = (user) => Boolean(user) && user.documentos_generados === 0;

// Menú Tutoriales / tutorial desde Sincronizar (19-oct-2026, mockup smartdx_mockups_tutorial_desde_sincronizar.html,
// opción A). La PRIMERA vez que la persona aprieta «Sincronizar camas» en un computador, si el tutorial de camas le
// sigue pendiente (la misma regla de cuenta de arriba, `tutorialCamasPendiente`: campo null, Modo Turno, no superuser
// y sin una sincronización aplicada), se abre el tutorial en repaso (con su X) y al cerrarlo o terminarlo cae en el
// diálogo real. No pide 3 documentos (eso es del obligatorio, R8): aparece al usar la función, no al entrar.
// - `movil`: el computador con la ventana angosta (cockpit del celular) no abre ESTE tutorial; el celular real tiene
//   el suyo en video vertical y NO se impone: se ofrece (`decidirOfertaTutorialCamasMovil`, más abajo).
// - `hechoEnEstaCarga`: completado en esta carga aunque `/me` todavía no traiga la fecha (R20 de #1664).
// - `yaOfrecido`: la marca local por usuario (`claveTutorialCamasDesdeSincronizar`), puesta al abrirlo: cerrarlo con
//   la X sin terminarlo no lo vuelve a abrir en el próximo «Sincronizar camas»; desde ahí el acceso es el enlace
//   «▶ Ver tutorial (2 min)» del diálogo y el menú del Turno.
export const claveTutorialCamasDesdeSincronizar = (userId) => `smartdx_tutorial_camas_desde_sincronizar_v1:${userId}`;

export const decidirTutorialCamasDesdeSincronizar = ({
  user,
  computador,
  movil = false,
  selectedOwner = null,
  hechoEnEstaCarga = false,
  yaOfrecido = false,
}) =>
  tutorialCamasPendiente(user) &&
  selectedOwner == null &&
  Boolean(computador) &&
  !movil &&
  !hechoEnEstaCarga &&
  !yaOfrecido;

// Dónde se ofrece el tutorial de camas a pedido EN EL COMPUTADOR (menú del Turno → Tutoriales, el enlace del diálogo
// de Sincronizar camas y el final de «Tu primer documento», #1859): el pop-up; el celular tiene su propio video vertical
// (`ofrecerTutorialCamasMovil`, más abajo). En el propio Turno y con el sistema del
// tutorial activo. Sin pedir documentos ni que esté pendiente: es un repaso. Menú Tutoriales: revisor v1 (19-oct,
// Sebastián): el superusuario también lo ve (para probarlo); lo AUTOMÁTICO (`tutorialCamasPendiente`) lo sigue excluyendo.
const puedeVerTutoriales = (user) => Boolean(user) && (sistemaTutorialActivo(user) || user.is_superuser === true);

export const ofrecerTutorialCamas = ({ user, computador, movil = false, selectedOwner = null }) =>
  puedeVerTutoriales(user) && selectedOwner == null && Boolean(computador) && !movil;

// «Tu primer documento» en el menú Tutoriales: mismo criterio (propio Turno; sin computador, porque ese tutorial
// también existe en el celular).
export const ofrecerTutorialPrimerDoc = ({ user, selectedOwner = null }) => puedeVerTutoriales(user) && selectedOwner == null;

// ─────────────────────────────────────────────────────────────────────────────
// CELULAR: video vertical con voz (paquete #8, mockup B de smartdx_mockups_tutorial_camas_celular.html).
// Cubre R1, R2, R3, R4, R10, R15 — .claude/specs/1664-tutorial-camas-celular/
// El celular OFRECE, no impone: sin pop-up obligatorio (decisión del 13-oct: «esperar al computador»). Y verlo aquí
// NO cuenta como visto en el computador (R12 v1.1): no hay POST a tutorial-camas/completado/ desde el celular.
// ─────────────────────────────────────────────────────────────────────────────

// Dónde está disponible a pedido (hoja Acciones → Tutoriales, y el enlace del diálogo de Sincronizar camas): el cockpit
// del celular (`movil`) en un aparato que no es computador, propio Turno y con el sistema del tutorial activo. Es un
// repaso: no pide estar pendiente. Mismo criterio de superusuario que el menú del computador (`puedeVerTutoriales`).
export const ofrecerTutorialCamasMovil = ({ user, computador, movil, selectedOwner = null }) =>
  puedeVerTutoriales(user) && selectedOwner == null && Boolean(movil) && !computador;

// La PRIMERA vez que aprieta «Sincronizar camas» en el celular, con el tutorial pendiente (la misma regla de cuenta,
// `tutorialCamasPendiente`), sale la hoja «Primera vez con tus camas». R3: una sola vez por cuenta y aparato (la marca
// `claveTutorialCamasDesdeSincronizar` se pone al MOSTRAR la hoja, no al elegir). R4: nunca con otro tutorial encima
// ni abierto antes en esta carga.
export const decidirOfertaTutorialCamasMovil = ({
  user,
  computador,
  movil,
  selectedOwner = null,
  otraCapa = false,
  otraCapaEnEstaCarga = false,
  hechoEnEstaCarga = false,
  yaOfrecido = false,
}) =>
  ofrecerTutorialCamasMovil({ user, computador, movil, selectedOwner }) &&
  tutorialCamasPendiente(user) &&
  !otraCapa &&
  !otraCapaEnEstaCarga &&
  !hechoEnEstaCarga &&
  !yaOfrecido;

// Página vertical propia (tools/tutorial_build/build_camas_celular.py). `docs=1` = la cuenta tiene 1 o más documentos
// (felicitación y voz c2_v0); `docs=0` = 0 o sin el dato (R10: portada neutra). La cifra nunca viaja en la URL.
// `origen` (R13) viaja solo como slug de una lista cerrada. Modo siempre repaso: el celular no impone.
export const urlTutorialCamasMovil = ({ tema, documentos = null, origen = "repaso" }) =>
  `/tutorial/sincronizar-camas-celular.html?theme=${tema === "light" ? "light" : "dark"}&modo=repaso` +
  `&docs=${Number.isFinite(documentos) && documentos >= 1 ? 1 : 0}` +
  `&origen=${origen === "oferta" || origen === "desde_sincronizar" || origen === "fin_tutorial1" ? origen : "repaso"}`;
